import type { ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { BookMarked, LogOut, PlusCircle, Ticket } from 'lucide-react';
import { cn } from '@/lib/cn';
import { usePortalAuth } from '@/portal/auth.store';

/**
 * A casca do portal.
 *
 * Deliberadamente pobre em relação ao `AppShell` da operação: três itens, sem
 * busca global, sem seletor de organização, sem menu de administração. Não é
 * economia de esforço — é a superfície. Cada controle a mais aqui é uma rota a
 * mais exposta na internet aberta a usuários autenticados não confiáveis.
 */

const ITENS = [
  { to: '/', rotulo: 'Meus chamados', icone: Ticket },
  { to: '/abrir', rotulo: 'Abrir chamado', icone: PlusCircle },
  { to: '/base', rotulo: 'Soluções', icone: BookMarked },
] as const;

export function PortalShell({ children }: { children: ReactNode }) {
  const contato = usePortalAuth((estado) => estado.contato);
  const caminho = useRouterState({ select: (estado) => estado.location.pathname });

  return (
    <div className="flex min-h-full flex-col bg-background text-foreground">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-tight text-gradient">Central Aptum</p>
            <p className="truncate text-xs text-muted-foreground">
              {contato?.empresa ?? 'Portal de atendimento'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-muted-foreground sm:inline">
              {contato?.nome}
            </span>
            <button
              type="button"
              onClick={() => void usePortalAuth.getState().sair()}
              className="inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-sm text-foreground hover:bg-accent"
            >
              <LogOut className="h-4 w-4" aria-hidden />
              Sair
            </button>
          </div>
        </div>

        <nav className="mx-auto flex max-w-5xl gap-1 px-2" aria-label="Seções do portal">
          {ITENS.map((item) => {
            const ativo = item.to === '/' ? caminho === '/' : caminho.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  'inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm',
                  ativo
                    ? 'border-primary font-medium text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <item.icone className="h-4 w-4" aria-hidden />
                {item.rotulo}
              </Link>
            );
          })}
        </nav>
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6">{children}</main>
    </div>
  );
}
